/**
 * useLiveReportDetailsState — close nav + scroll machine for NPS / Referral modals.
 *
 * `useReducer` owns modal visibility. Legacy parity: ≥50% scroll, wait 1s, then open NPS
 * if eligible, else Referral. Eligibility flags are read from refs so the scroll handler stays stable.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { type NativeScrollEvent, type NativeSyntheticEvent } from "react-native"
import { useNavigation } from "@react-navigation/native"

export type UseLiveReportDetailsStateConfig = {
    /** Skip both modals for admin/tutor (legacy `shouldCheckNPS`). */
    shouldCheckModals: boolean
    canShowNPS: boolean
    canShowReferralModal: boolean
}

type ModalState = {
    showNpsModal: boolean
    showReferralModal: boolean
}

type ModalAction =
    { type: "OPEN_NPS" } | { type: "OPEN_REFERRAL" } | { type: "DISMISS_NPS" } | { type: "DISMISS_REFERRAL" }

const initialState: ModalState = {
    showNpsModal: false,
    showReferralModal: false,
}

function modalReducer(state: ModalState, action: ModalAction): ModalState {
    switch (action.type) {
        case "OPEN_NPS":
            return { ...state, showNpsModal: true }
        case "OPEN_REFERRAL":
            return { ...state, showReferralModal: true }
        case "DISMISS_NPS":
            return { ...state, showNpsModal: false }
        case "DISMISS_REFERRAL":
            return { ...state, showReferralModal: false }
        default:
            return state
    }
}

export function useLiveReportDetailsState(config: UseLiveReportDetailsStateConfig) {
    const { shouldCheckModals, canShowNPS, canShowReferralModal } = config
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string) => void
    }>()

    const [state, dispatch] = useReducer(modalReducer, initialState)

    const shouldCheckRef = useRef(shouldCheckModals)
    const canShowNPSRef = useRef(canShowNPS)
    const canShowReferralRef = useRef(canShowReferralModal)
    shouldCheckRef.current = shouldCheckModals
    canShowNPSRef.current = canShowNPS
    canShowReferralRef.current = canShowReferralModal

    const hasTriggeredNPS = useRef(false)
    const hasTriggeredReferral = useRef(false)
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const resetTriggers = useCallback(() => {
        hasTriggeredNPS.current = false
        hasTriggeredReferral.current = false
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current)
            scrollTimeoutRef.current = null
        }
    }, [])

    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
        }
    }, [])

    const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent
        const denom = contentSize.height - layoutMeasurement.height
        if (denom <= 0) return
        const scrollPercentage = (contentOffset.y / denom) * 100

        if (scrollPercentage >= 50 && shouldCheckRef.current && canShowNPSRef.current && !hasTriggeredNPS.current) {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
            scrollTimeoutRef.current = setTimeout(() => {
                hasTriggeredNPS.current = true
                dispatch({ type: "OPEN_NPS" })
            }, 1000)
        } else if (
            scrollPercentage >= 50 &&
            shouldCheckRef.current &&
            !canShowNPSRef.current &&
            canShowReferralRef.current &&
            !hasTriggeredReferral.current &&
            !hasTriggeredNPS.current
        ) {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
            scrollTimeoutRef.current = setTimeout(() => {
                hasTriggeredReferral.current = true
                dispatch({ type: "OPEN_REFERRAL" })
            }, 1000)
        }
    }, [])

    const close = useCallback(() => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation])

    const dismissNps = useCallback(() => dispatch({ type: "DISMISS_NPS" }), [])
    const dismissReferral = useCallback(() => dispatch({ type: "DISMISS_REFERRAL" }), [])

    const actions = useMemo(
        () => ({
            close,
            dismissNps,
            dismissReferral,
            resetTriggers,
        }),
        [close, dismissNps, dismissReferral, resetTriggers]
    )

    return {
        actions,
        onScroll,
        showNpsModal: state.showNpsModal,
        showReferralModal: state.showReferralModal,
    }
}
